import { useState } from "react"
import { Link } from "react-router-dom"
import type { RelayInfo, RelayStatus } from "@/nostr/types"
import { supportsSearch } from "@/nostr/relayInfo"
import { useRelays } from "@/state/RelayContext"
import { useNostr } from "@/state/NostrContext"
import { simplifyUrl } from "@/utils/url"
import { RelayIcon } from "./Avatar"
import { Icon } from "./Icon"

export interface RelayCardProps {
  url: string
  info?: RelayInfo
  status?: RelayStatus
  dense?: boolean
}

function statusLabel(status?: RelayStatus): { label: string; tone: string } {
  switch (status?.state) {
    case "connected":
      return { label: status.latency ? `${status.latency} ms` : "connected", tone: "ok" }
    case "connecting":
      return { label: "connecting", tone: "warn" }
    case "reconnecting":
      return { label: "reconnecting", tone: "warn" }
    case "error":
      return { label: "unreachable", tone: "bad" }
    case "closed":
      return { label: "disconnected", tone: "bad" }
    default:
      return { label: "idle", tone: "muted" }
  }
}

/** Relay summary used by explore, search and the relay library. */
export function RelayCard({ url, info, status, dense = false }: RelayCardProps) {
  const { favoriteRelays, addFavoriteRelays, removeFavoriteRelays, relaySets, addRelaysToSet } = useRelays()
  const { statuses } = useNostr()
  const [busy, setBusy] = useState(false)
  const [showSets, setShowSets] = useState(false)
  const favorite = favoriteRelays.includes(url)
  const live = status ?? statuses.find((item) => item.url === url)
  const badge = statusLabel(live)
  const nips = info?.supported_nips ?? []

  const toggleFavorite = async () => {
    setBusy(true)
    try {
      if (favorite) await removeFavoriteRelays([url])
      else await addFavoriteRelays([url])
    } finally {
      setBusy(false)
    }
  }

  return (
    <article className={`relay-card ${dense ? "relay-card-dense" : ""}`}>
      <div className="relay-card-head">
        <RelayIcon url={url} icon={info?.icon} size={36} />
        <div className="relay-card-title">
          <Link to={`/relay/${encodeURIComponent(url)}`} className="relay-card-name">
            {info?.name?.trim() || simplifyUrl(url)}
          </Link>
          <span className="relay-card-url">{simplifyUrl(url)}</span>
        </div>
        <span className={`status-pill status-${badge.tone}`} title={live?.lastError ?? undefined}>
          {badge.label}
        </span>
      </div>

      {info?.description && <p className="relay-card-description">{info.description}</p>}

      <div className="relay-card-meta">
        {info?.limitation?.auth_required && <span className="badge">auth required</span>}
        {info?.limitation?.payment_required && <span className="badge">paid</span>}
        {supportsSearch(info) && <span className="badge badge-primary">NIP-50</span>}
        {nips.slice(0, 6).map((nip) => (
          <span key={nip} className="badge">
            NIP-{nip}
          </span>
        ))}
        {nips.length > 6 && <span className="badge">+{nips.length - 6}</span>}
      </div>

      <div className="relay-card-actions">
        <button
          type="button"
          className={`btn btn-small ${favorite ? "btn-ghost" : "btn-primary"}`}
          onClick={toggleFavorite}
          disabled={busy}
        >
          <Icon name={favorite ? "check" : "plus"} size={14} />
          {favorite ? "In my relays" : "Add to my relays"}
        </button>
        <Link to={`/relay/${encodeURIComponent(url)}`} className="btn btn-small btn-ghost">
          <Icon name="relay" size={14} /> Open feed
        </Link>
        {relaySets.length > 0 && (
          <div className="relay-card-set">
            <button
              type="button"
              className="btn btn-small btn-ghost"
              onClick={() => setShowSets((value) => !value)}
              aria-expanded={showSets}
            >
              <Icon name="plus" size={14} /> Add to set
            </button>
            {showSets && (
              <div className="relay-card-set-list" role="menu">
                {relaySets.map((set) => (
                  <button
                    key={set.id}
                    type="button"
                    role="menuitem"
                    className="relay-card-set-item"
                    onClick={async () => {
                      setBusy(true)
                      try {
                        await addRelaysToSet(set.id, [url])
                        setShowSets(false)
                      } finally {
                        setBusy(false)
                      }
                    }}
                    disabled={busy}
                  >
                    {set.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  )
}
